import React, { useEffect, useState } from 'react';
import { isPasswordValid } from '../../../utils/passwordPolicy';
import PasswordRequirements from './PasswordRequirements';

export const RESET_RESEND_COOLDOWN_SECONDS = 60;

interface ForgotPasswordProps {
  email: string;
  setEmail: (email: string) => void;
  /** Sends the reset code. Rejects when no code could be requested (the form stays on step 1). */
  handleForgotPassword: () => Promise<void>;
  handleResetPassword: (code: string, newPassword: string) => Promise<void>;
  setView: (view: string) => void;
  error: string | null;
  isLoading: boolean;
}

const inputClass = 'w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10';

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  email,
  setEmail,
  handleForgotPassword,
  handleResetPassword,
  setView,
  error,
  isLoading
}) => {
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [cooldown, setCooldown] = useState<number>(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const passwordMismatch = Boolean(confirmPassword) && newPassword !== confirmPassword;
  const canReset = Boolean(verificationCode.trim()) && isPasswordValid(newPassword) && newPassword === confirmPassword;

  const requestCode = async (): Promise<void> => {
    try {
      await handleForgotPassword();
      setIsCodeSent(true);
      setCooldown(RESET_RESEND_COOLDOWN_SECONDS);
    } catch {
      // The container shows the error; stay on the current step
    }
  };

  const handleSendCodeFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void requestCode();
  };

  const handleResetFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canReset) return;
    void handleResetPassword(verificationCode.trim(), newPassword);
  };

  const useDifferentEmail = () => {
    setIsCodeSent(false);
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="w-full max-w-sm py-8 md:py-10">
      <h2 className="text-2xl font-semibold mb-1 text-slate-800 text-left">
        {isCodeSent ? 'Reset your password' : 'Forgot password'}
      </h2>
      <p className="text-gray-500 mb-6 text-sm text-left">
        {isCodeSent
          ? `If an account exists for ${email}, we've sent it a verification code.`
          : "Enter your email and we'll send you a code to reset your password"}
      </p>

      {error && <div className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded mb-6 text-sm" role="alert">{error}</div>}

      {!isCodeSent ? (
        <form onSubmit={handleSendCodeFormSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label className="font-medium text-slate-800 text-sm text-left" htmlFor="email">Email address</label>
            <input
              className={inputClass}
              type="email"
              id="email"
              autoComplete="username"
              placeholder="name@practice.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className={`w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-sm font-medium transition ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            disabled={isLoading || !email.trim()}
          >
            {isLoading ? 'Sending...' : 'Send reset code'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleResetFormSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label className="font-medium text-slate-800 text-sm text-left" htmlFor="verificationCode">
              Verification code
            </label>
            <input
              className={inputClass}
              type="text"
              id="verificationCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Enter the code we sent to your email"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-medium text-slate-800 text-sm text-left" htmlFor="newPassword">
              New password
            </label>
            <input
              className={inputClass}
              type="password"
              id="newPassword"
              autoComplete="new-password"
              placeholder="Enter your new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <PasswordRequirements password={newPassword} />
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-medium text-slate-800 text-sm text-left" htmlFor="confirmPassword">
              Confirm password
            </label>
            <input
              className={`${inputClass} ${passwordMismatch ? 'border-red-300' : ''}`}
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {passwordMismatch && <div className="text-xs text-red-500">Passwords don't match</div>}
          </div>

          <button
            type="submit"
            className={`w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-sm font-medium transition ${isLoading || !canReset ? 'opacity-70 cursor-not-allowed' : ''}`}
            disabled={isLoading || !canReset}
          >
            {isLoading ? 'Resetting...' : 'Reset password'}
          </button>

          <div className="flex justify-between text-sm">
            <button
              type="button"
              onClick={() => { void requestCode(); }}
              disabled={isLoading || cooldown > 0}
              className={`font-medium ${cooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'}`}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
            <button type="button" onClick={useDifferentEmail} className="text-blue-600 hover:text-blue-700 font-medium">
              Use a different email
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 text-center text-sm">
        <p>
          <button
            type="button"
            onClick={() => setView('signin')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Back to sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
